import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { FiPlus, FiArrowLeft, FiEdit, FiSearch, FiList, FiColumns } from 'react-icons/fi';

const STATUSES = ['To Do', 'In Progress', 'In Review', 'QA', 'Pre Production', 'Done'];
const STATUS_COLORS = { 'To Do': '#6b7280', 'In Progress': '#3b82f6', 'In Review': '#f59e0b', 'QA': '#8b5cf6', 'Pre Production': '#ec4899', 'Done': '#10b981' };
const PRIORITY_COLORS = { Low: '#6b7280', Medium: '#3b82f6', High: '#f59e0b', Critical: '#ef4444' };
const TYPE_ICONS = { Bug: '🐛', Task: '✅', Story: '📖', Epic: '⚡' };

const ProjectBoard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminHR = user?.role === 'admin' || user?.role === 'hr';
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [view, setView] = useState('board'); // 'board' or 'list'
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterEpic, setFilterEpic] = useState('');
  const [search, setSearch] = useState('');
  const [taskForm, setTaskForm] = useState({
    title: '', description: '', type: 'Task', priority: 'Medium', assignee: '',
    dueDate: '', parent: '', storyPoints: '', labels: '',
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [projRes, tasksRes, empRes] = await Promise.all([
        api.get(`/projects/${id}`),
        api.get('/tasks', { params: { project: id, limit: 200 } }),
        api.get('/employees', { params: { limit: 200 } }),
      ]);
      setProject(projRes.data);
      setTasks(tasksRes.data.tasks || []);
      setEmployees(empRes.data.employees || []);
    } catch (err) {
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const epics = tasks.filter(t => t.type === 'Epic');

  const handleAddTask = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...taskForm, project: id };
      if (taskForm.labels) payload.labels = taskForm.labels.split(',').map(l => l.trim()).filter(Boolean);
      if (taskForm.storyPoints) payload.storyPoints = Number(taskForm.storyPoints);
      if (!taskForm.parent) delete payload.parent;
      await api.post('/tasks', payload);
      toast.success('Task created');
      setShowForm(false);
      setTaskForm({ title: '', description: '', type: 'Task', priority: 'Medium', assignee: '', dueDate: '', parent: '', storyPoints: '', labels: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create task');
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await api.put(`/tasks/${taskId}/status`, { status: newStatus });
      setTasks(prev => prev.map(t => t._id === taskId ? { ...t, status: newStatus } : t));
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const filteredTasks = tasks.filter(t => {
    if (filterAssignee && t.assignee?._id !== filterAssignee) return false;
    if (filterPriority && t.priority !== filterPriority) return false;
    if (filterType && t.type !== filterType) return false;
    if (filterEpic) {
      if (filterEpic === 'none' && t.parent) return false;
      if (filterEpic !== 'none' && (t.parent?._id || t.parent) !== filterEpic && t._id !== filterEpic) return false;
    }
    if (search && !t.title.toLowerCase().includes(search.toLowerCase()) && !t.taskId.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  if (loading) return <div className="loading">Loading...</div>;
  if (!project) return <div className="loading">Project not found</div>;

  // Group tasks by epic for swimlane view
  const epicGroups = [];
  const usedIds = new Set();

  // First, add epic swimlanes
  epics.forEach(epic => {
    if (!filteredTasks.find(t => t._id === epic._id)) return;
    const children = filteredTasks.filter(t => (t.parent?._id || t.parent) === epic._id);
    epicGroups.push({ epic, tasks: [epic, ...children] });
    usedIds.add(epic._id);
    children.forEach(c => usedIds.add(c._id));
  });

  // Then tasks under epics that aren't the epic itself
  filteredTasks.forEach(t => {
    if (usedIds.has(t._id)) return;
    if (t.parent) {
      const parentEpic = epics.find(e => e._id === (t.parent?._id || t.parent));
      if (parentEpic) {
        const group = epicGroups.find(g => g.epic._id === parentEpic._id);
        if (group && !group.tasks.find(gt => gt._id === t._id)) {
          group.tasks.push(t);
        }
        usedIds.add(t._id);
      }
    }
  });

  // Remaining unlinked tasks
  const unlinkedTasks = filteredTasks.filter(t => !usedIds.has(t._id));
  if (unlinkedTasks.length > 0) {
    epicGroups.push({ epic: null, tasks: unlinkedTasks });
  }

  const renderCard = (task) => (
    <div key={task._id} className={`kanban-card ${task.type === 'Epic' ? 'epic-card' : ''}`} onClick={() => navigate(`/projects/${id}/tasks/${task._id}`)}>
      <div className="kanban-card-header">
        <span className="kanban-task-id">{task.taskId}</span>
        <span className={`priority-dot priority-${task.priority.toLowerCase()}`} title={task.priority} />
      </div>
      <div className="kanban-card-title">{task.title}</div>
      <div className="kanban-card-meta">
        <span className="kanban-type-badge">{TYPE_ICONS[task.type]} {task.type}</span>
        {task.storyPoints > 0 && <span className="story-points-badge">{task.storyPoints} SP</span>}
        {task.dueDate && (
          <span className={`kanban-due ${new Date(task.dueDate) < new Date() && task.status !== 'Done' ? 'overdue' : ''}`}>
            {new Date(task.dueDate).toLocaleDateString()}
          </span>
        )}
      </div>
      {task.labels?.length > 0 && (
        <div className="kanban-labels">
          {task.labels.map((l, i) => <span key={i} className="kanban-label">{l}</span>)}
        </div>
      )}
      {task.type === 'Epic' && task.childCount > 0 && (
        <div className="epic-progress-bar">
          <div className="epic-progress-fill" style={{ width: `${Math.round((task.childDone / task.childCount) * 100)}%` }} />
          <span className="epic-progress-text">{task.childDone}/{task.childCount}</span>
        </div>
      )}
      <div className="kanban-card-footer">
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {task.assignee ? (
            <span className="kanban-avatar" title={task.assignee.name}>
              {task.assignee.name.charAt(0).toUpperCase()}
            </span>
          ) : <span className="kanban-unassigned">Unassigned</span>}
          {task.parent && <span className="kanban-parent-tag">{task.parent.taskId || ''}</span>}
        </div>
        <div className="kanban-move-btns" onClick={e => e.stopPropagation()}>
          {STATUSES.filter(s => s !== task.status).map(s => (
            <button
              key={s}
              className="kanban-move-btn"
              style={{ borderColor: STATUS_COLORS[s] }}
              title={`Move to ${s}`}
              onClick={() => handleStatusChange(task._id, s)}
            >
              {s.split(' ').map(w => w[0]).join('')}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button className="btn-sm btn-secondary" onClick={() => navigate('/projects')}><FiArrowLeft /></button>
          <div>
            <h1>{project.name}</h1>
            <span className="project-key-badge">{project.key}</span>
            <span style={{ marginLeft: '0.75rem', fontSize: '0.85rem', color: '#6b7280' }}>
              {tasks.length} tasks &middot; {epics.length} epics
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <div className="view-toggle">
            <button className={`view-btn ${view === 'board' ? 'active' : ''}`} onClick={() => setView('board')} title="Board"><FiColumns /></button>
            <button className={`view-btn ${view === 'list' ? 'active' : ''}`} onClick={() => setView('list')} title="List"><FiList /></button>
          </div>
          {isAdminHR && (
            <button className="btn-sm btn-warning" onClick={() => navigate(`/projects/${id}/edit`)}><FiEdit /></button>
          )}
          <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
            <FiPlus /> {showForm ? 'Cancel' : 'Add Task'}
          </button>
        </div>
      </div>

      {/* Quick Add Task Form */}
      {showForm && (
        <div className="form-card" style={{ marginBottom: '1rem' }}>
          <h3>New Task</h3>
          <form onSubmit={handleAddTask}>
            <div className="form-grid">
              <div className="form-group">
                <label>Title *</label>
                <input value={taskForm.title} onChange={e => setTaskForm({ ...taskForm, title: e.target.value })} required />
              </div>
              <div className="form-group">
                <label>Type</label>
                <select value={taskForm.type} onChange={e => setTaskForm({ ...taskForm, type: e.target.value })}>
                  {['Epic', 'Story', 'Task', 'Bug'].map(t => <option key={t} value={t}>{TYPE_ICONS[t]} {t}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Priority</label>
                <select value={taskForm.priority} onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })}>
                  {['Low', 'Medium', 'High', 'Critical'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Parent Epic</label>
                <select value={taskForm.parent} onChange={e => setTaskForm({ ...taskForm, parent: e.target.value })}>
                  <option value="">No Epic (standalone)</option>
                  {epics.map(e => <option key={e._id} value={e._id}>{e.taskId} - {e.title}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Assignee</label>
                <select value={taskForm.assignee} onChange={e => setTaskForm({ ...taskForm, assignee: e.target.value })}>
                  <option value="">Unassigned</option>
                  {employees.map(emp => <option key={emp._id} value={emp._id}>{emp.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Due Date</label>
                <input type="date" value={taskForm.dueDate} onChange={e => setTaskForm({ ...taskForm, dueDate: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Story Points</label>
                <input type="number" min="0" value={taskForm.storyPoints} onChange={e => setTaskForm({ ...taskForm, storyPoints: e.target.value })} placeholder="0" />
              </div>
              <div className="form-group">
                <label>Labels (comma-separated)</label>
                <input value={taskForm.labels} onChange={e => setTaskForm({ ...taskForm, labels: e.target.value })} placeholder="frontend, api, urgent" />
              </div>
              <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                <label>Description</label>
                <textarea value={taskForm.description} onChange={e => setTaskForm({ ...taskForm, description: e.target.value })} rows={2} />
              </div>
            </div>
            <button type="submit" className="btn-primary" style={{ marginTop: '0.75rem' }}>Create {taskForm.type}</button>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="filters-row" style={{ marginBottom: '1rem' }}>
        <div className="search-box">
          <FiSearch />
          <input placeholder="Search tasks..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select value={filterEpic} onChange={e => setFilterEpic(e.target.value)}>
          <option value="">All Epics</option>
          <option value="none">No Epic</option>
          {epics.map(e => <option key={e._id} value={e._id}>{e.taskId} - {e.title}</option>)}
        </select>
        <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}>
          <option value="">All Assignees</option>
          {employees.map(emp => <option key={emp._id} value={emp._id}>{emp.name}</option>)}
        </select>
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
          <option value="">All Priorities</option>
          {['Low', 'Medium', 'High', 'Critical'].map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          {['Epic', 'Story', 'Task', 'Bug'].map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      {/* Board View */}
      {view === 'board' && (
        <div>
          {epicGroups.map((group, gi) => (
            <div key={gi} className="epic-swimlane">
              {group.epic ? (
                <div className="epic-swimlane-header" onClick={() => navigate(`/projects/${id}/tasks/${group.epic._id}`)}>
                  <span>{TYPE_ICONS.Epic}</span>
                  <span className="kanban-task-id">{group.epic.taskId}</span>
                  <strong>{group.epic.title}</strong>
                  <span className="epic-swimlane-count">{group.tasks.length - 1} items</span>
                  {group.epic.childCount > 0 && (
                    <span className="epic-swimlane-progress">
                      {Math.round((group.epic.childDone / group.epic.childCount) * 100)}% done
                    </span>
                  )}
                </div>
              ) : (
                <div className="epic-swimlane-header no-epic">
                  <strong>Backlog (No Epic)</strong>
                  <span className="epic-swimlane-count">{group.tasks.length} items</span>
                </div>
              )}
              <div className="kanban-board">
                {STATUSES.map(status => {
                  const columnTasks = group.tasks.filter(t => t.status === status && (group.epic ? t._id !== group.epic._id || t.type !== 'Epic' : true));
                  // For epics in their own swimlane, also show the epic card in its status column
                  const epicInColumn = group.epic && group.epic.status === status;
                  return (
                    <div key={status} className="kanban-column">
                      <div className="kanban-column-header" style={{ borderTopColor: STATUS_COLORS[status] }}>
                        <span>{status}</span>
                        <span className="kanban-count">{columnTasks.length + (epicInColumn ? 1 : 0)}</span>
                      </div>
                      <div className="kanban-cards">
                        {epicInColumn && renderCard(group.epic)}
                        {columnTasks.filter(t => t._id !== group.epic?._id).map(renderCard)}
                        {columnTasks.length === 0 && !epicInColumn && (
                          <div className="kanban-empty">No tasks</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {epicGroups.length === 0 && (
            <div className="kanban-board">
              {STATUSES.map(status => (
                <div key={status} className="kanban-column">
                  <div className="kanban-column-header" style={{ borderTopColor: STATUS_COLORS[status] }}>
                    <span>{status}</span><span className="kanban-count">0</span>
                  </div>
                  <div className="kanban-cards"><div className="kanban-empty">No tasks</div></div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* List View */}
      {view === 'list' && (
        <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Type</th>
              <th>Title</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Assignee</th>
              <th>Epic</th>
              <th>SP</th>
              <th>Due Date</th>
            </tr>
          </thead>
          <tbody>
            {filteredTasks.map(task => (
              <tr key={task._id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/projects/${id}/tasks/${task._id}`)}>
                <td><span className="kanban-task-id">{task.taskId}</span></td>
                <td>{TYPE_ICONS[task.type]} {task.type}</td>
                <td>
                  <strong>{task.title}</strong>
                  {task.labels?.length > 0 && (
                    <div className="kanban-labels" style={{ marginTop: '2px' }}>
                      {task.labels.map((l, i) => <span key={i} className="kanban-label">{l}</span>)}
                    </div>
                  )}
                </td>
                <td>
                  <span className={`status-badge`} style={{ background: STATUS_COLORS[task.status] + '22', color: STATUS_COLORS[task.status] }}>
                    {task.status}
                  </span>
                </td>
                <td><span style={{ color: PRIORITY_COLORS[task.priority], fontWeight: 600 }}>{task.priority}</span></td>
                <td>{task.assignee?.name || '-'}</td>
                <td>{task.parent?.taskId || '-'}</td>
                <td>{task.storyPoints || '-'}</td>
                <td className={task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'Done' ? 'overdue-text' : ''}>
                  {task.dueDate ? new Date(task.dueDate).toLocaleDateString() : '-'}
                </td>
              </tr>
            ))}
            {filteredTasks.length === 0 && (
              <tr><td colSpan="9" style={{ textAlign: 'center' }}>No tasks found</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ProjectBoard;
